import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { useMutation } from '@apollo/client'
import { NUDGE_GOLFER_INTAKE } from './api-hooks'
import { get } from 'lodash'
import hasNonNullProperty from 'lib/utils/has-non-null-property'

const useStyles = makeStyles(theme => ({
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: theme.spacing(3),
    },
  },
  profileCopy: {
    flex: 1,
    minWidth: 0,
  },
  profileCaption: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  profileButton: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    width: 'auto',
    borderRadius: 20,
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1.25, 3.5),
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
    [theme.breakpoints.only('xs')]: {
      alignSelf: 'stretch',
      width: '100%',
      justifyContent: 'center',
    },
  },
}))

const GolferProfileCta = ({ student }) => {
  const classes = useStyles()
  const [mutationSent, setMutationSent] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const [requestProfileFulfillment] = useMutation(NUDGE_GOLFER_INTAKE)

  const hasCompletedProfile = hasNonNullProperty(student?.golferProfile)
  const { hasBeenIntakeNudged } = student
  if (hasBeenIntakeNudged || hasCompletedProfile) return null

  const handleProfileNudge = async () => {
    try {
      const { data } = await requestProfileFulfillment({
        variables: { studentIds: [student.id] },
      })
      const success = get(data, ['SendGolferIntakeNudge', 'success'])
      const message = get(data, ['SendGolferIntakeNudge', 'message'])
      if (!success) {
        return snackbarError(
          message ||
            "This golfer has already been nudged. You're unable to send a new profile nudge",
        )
      }
      snackbarSuccess(
        `${student.firstName} ${student.lastName} has been sent a request to complete their profile`,
      )
      setTimeout(() => {
        setMutationSent(true)
      }, 1000)
    } catch (e) {
      snackbarError('Unable to send request to complete profile')
    }
  }

  if (mutationSent) return null

  return (
    <Box className={classes.profileSection}>
      <Box className={classes.profileCopy}>
        <Typography component="p" className={classes.profileCaption}>
          Request that your student complete their golfer profile. This form
          will help cater your instruction and learn more about their past golf
          experience.
        </Typography>
      </Box>
      <Button
        variant="contained"
        color="primary"
        className={classes.profileButton}
        onClick={handleProfileNudge}
      >
        Request
      </Button>
    </Box>
  )
}

export default GolferProfileCta
